import { Entity } from '@backstage/catalog-model';
import * as yup from 'yup';

export interface ValidationError {
  path: string;
  message: string;
  severity: 'error' | 'warning';
}

// Helper to convert Yup errors to ValidationError format (all as warnings since frontend doesn't block imports)
function convertYupErrors(yupError: yup.ValidationError): ValidationError[] {
  return yupError.inner.map((err) => ({
    path: err.path || '',
    message: err.message,
    severity: 'warning' as const, // All as warnings since frontend doesn't block imports
  }));
}

// Component validation schema with Yup - all validations in one place, easy to extend
const componentSchema = yup.object({
  metadata: yup.object({
    name: yup.string().required('Component name is required. Please fill in the name field.'),
    description: yup
      .string()
      .optional()
      .test('description-recommended', 'Component description is required. Please add a description of the component.', function (value) {
        if (!value) {
          return this.createError({ message: 'Component description is required. Please add a description of the component.' });
        }
        return true;
      }),
    links: yup.array().optional(),
    annotations: yup
      .object({
        'backstage.io/health-check': yup
          .string()
          .optional()
          .test('health-check-recommended', 'Health check URL is recommended. Please add backstage.io/health-check annotation with CI/CD pipeline URL.', function (value) {
            if (!value) {
              return this.createError({ message: 'Health check URL is recommended. Please add backstage.io/health-check annotation with CI/CD pipeline URL.' });
            }
            return true;
          }),
        'backstage.io/security-reports': yup
          .string()
          .optional()
          .test('security-reports-recommended', 'Security reports are recommended. Please add backstage.io/security-reports annotation with security audit reports.', function (value) {
            if (!value) {
              return this.createError({ message: 'Security reports are recommended. Please add backstage.io/security-reports annotation with security audit reports.' });
            }
            return true;
          })
          .test('security-reports-format', 'Security reports must be a JSON array. Format: [{"name": "Report Name", "file": "report.html"}]', function (value) {
            if (!value) return true;
            try {
              const config = JSON.parse(value);
              if (!Array.isArray(config)) {
                return this.createError({ message: 'Security reports must be a JSON array. Format: [{"name": "Report Name", "file": "report.html"}]' });
              }
              if (config.length === 0) {
                return this.createError({ message: 'Security reports array is empty. Please add at least one report configuration.' });
              }
              // Validate each report object
              for (let i = 0; i < config.length; i++) {
                const report = config[i];
                if (!report.name || !report.file) {
                  return this.createError({ message: `Report at index ${i} must have "name" and "file" properties.` });
                }
              }
              return true;
            } catch {
              return this.createError({ message: 'Security reports must be valid JSON. Please check the format.' });
            }
          }),
        'backstage.io/trivy-scan': yup
          .string()
          .optional()
          .test('trivy-scan-recommended', 'Trivy scan configuration is recommended. Please add backstage.io/trivy-scan annotation (set to "enabled" if using Trivy).', function (value) {
            if (!value) {
              return this.createError({ message: 'Trivy scan configuration is recommended. Please add backstage.io/trivy-scan annotation (set to "enabled" if using Trivy).' });
            }
            return true;
          }),
        'backstage.io/trivy-config': yup
          .string()
          .optional()
          .test('trivy-config-recommended', 'Trivy config is recommended. Please add backstage.io/trivy-config annotation with job name patterns.', function (value) {
            if (!value) {
              return this.createError({ message: 'Trivy config is recommended. Please add backstage.io/trivy-config annotation with job name patterns.' });
            }
            return true;
          })
          .test('trivy-config-format', 'Trivy config must be a JSON array of job name patterns or a string. Format: ["security_test", "trivy"] or "security_test"', function (value) {
            if (!value) return true;
            try {
              const config = JSON.parse(value);
              if (!Array.isArray(config) && typeof config !== 'string') {
                return this.createError({ message: 'Trivy config must be a JSON array of job name patterns or a string. Format: ["security_test", "trivy"] or "security_test"' });
              }
              return true;
            } catch {
              return this.createError({ message: 'Trivy config must be valid JSON. Please check the format.' });
            }
          }),
        'backstage.io/linter-configured': yup
          .string()
          .optional()
          .test('linter-configured-recommended', 'Linter configuration status is recommended. Please add backstage.io/linter-configured annotation.', function (value) {
            if (!value) {
              return this.createError({ message: 'Linter configuration status is recommended. Please add backstage.io/linter-configured annotation.' });
            }
            return true;
          }),
        'backstage.io/linter-eslint': yup
          .string()
          .optional()
          .test('linter-eslint-recommended', 'ESLint configuration status is recommended. Please add backstage.io/linter-eslint annotation (set to "enabled" if using ESLint).', function (value) {
            if (!value) {
              return this.createError({ message: 'ESLint configuration status is recommended. Please add backstage.io/linter-eslint annotation (set to "enabled" if using ESLint).' });
            }
            return true;
          }),
        'backstage.io/linter-prettier': yup
          .string()
          .optional()
          .test('linter-prettier-recommended', 'Prettier configuration status is recommended. Please add backstage.io/linter-prettier annotation (set to "enabled" if using Prettier).', function (value) {
            if (!value) {
              return this.createError({ message: 'Prettier configuration status is recommended. Please add backstage.io/linter-prettier annotation (set to "enabled" if using Prettier).' });
            }
            return true;
          }),
        'sonarqube.org/instance': yup
          .string()
          .optional()
          .test('sonarqube-instance-recommended', 'SonarQube instance is recommended. Please add sonarqube.org/instance annotation with SonarQube instance URL.', function (value) {
            if (!value) {
              return this.createError({ message: 'SonarQube instance is recommended. Please add sonarqube.org/instance annotation with SonarQube instance URL.' });
            }
            return true;
          }),
        'sonarqube.org/project-key': yup
          .string()
          .optional()
          .test('sonarqube-project-key-recommended', 'SonarQube project key is recommended. Please add sonarqube.org/project-key annotation with SonarQube project identifier.', function (value) {
            if (!value) {
              return this.createError({ message: 'SonarQube project key is recommended. Please add sonarqube.org/project-key annotation with SonarQube project identifier.' });
            }
            return true;
          }),
        'gitlab.com/project-id': yup
          .string()
          .optional()
          .test('gitlab-project-id-recommended', 'GitLab project ID is required. Please add gitlab.com/project-id annotation with your GitLab project ID.', function (value) {
            if (!value) {
              return this.createError({ message: 'GitLab project ID is required. Please add gitlab.com/project-id annotation with your GitLab project ID.' });
            }
            return true;
          })
          .test('gitlab-project-id-format', 'GitLab project ID must be a valid numeric ID or string. Please check the format.', function (value) {
            if (!value) return true;
            // Allow numeric string (e.g., "543") or just numbers
            if (!/^\d+$/.test(value.trim())) {
              return this.createError({ message: 'GitLab project ID must be a valid numeric ID. Please check the format.' });
            }
            return true;
          }),
        // Test coverage annotations
        'backstage.io/test-framework': yup.string().optional(),
        'backstage.io/test-coverage-available': yup
          .string()
          .optional()
          .test('test-coverage-available-recommended', 'Test coverage is not available. Please set up tests and coverage reporting, then set backstage.io/test-coverage-available to "true".', function (value) {
            if (!value || value === 'false') {
              return this.createError({ message: 'Test coverage is not available. Please set up tests and coverage reporting, then set backstage.io/test-coverage-available to "true".' });
            }
            return true;
          }),
        'backstage.io/test-coverage': yup.string().optional(),
        'pagespeed/website-url': yup.string().optional(),
        // Sentry integration annotations
        'sentry.io/organization': yup
          .string()
          .optional()
          .test('sentry-organization-recommended', 'Sentry organization is recommended. Please add sentry.io/organization annotation with your Sentry organization name.', function (value) {
            if (!value) {
              return this.createError({ message: 'Sentry organization is recommended. Please add sentry.io/organization annotation with your Sentry organization name.' });
            }
            return true;
          }),
        'sentry.io/project-slug': yup
          .string()
          .optional()
          .test('sentry-project-slug-recommended', 'Sentry project slug is recommended. Please add sentry.io/project-slug annotation with your Sentry project slug.', function (value) {
            if (!value) {
              return this.createError({ message: 'Sentry project slug is recommended. Please add sentry.io/project-slug annotation with your Sentry project slug.' });
            }
            return true;
          }),
        'sentry.io/api-url': yup
          .string()
          .optional()
          .test('sentry-api-url-recommended', 'Sentry API URL is recommended for self-hosted Sentry. Please add sentry.io/api-url annotation with your Sentry instance URL.', function (value) {
            if (!value) {
              return this.createError({ message: 'Sentry API URL is recommended for self-hosted Sentry. Please add sentry.io/api-url annotation with your Sentry instance URL.' });
            }
            return true;
          }),
        'sentry.io/token': yup
          .string()
          .optional()
          .test('sentry-token-recommended', 'Sentry API token is recommended for accessing private projects. Please add sentry.io/token annotation.', function (value) {
            if (!value) {
              return this.createError({ message: 'Sentry API token is recommended for accessing private projects. Please add sentry.io/token annotation.' });
            }
            return true;
          }),
      })
      .test('trivy-consistency', 'Trivy config is required when Trivy scan is enabled. Please add backstage.io/trivy-config annotation.', function (annotations) {
        const ann = annotations as Record<string, string> | undefined;
        if (ann?.['backstage.io/trivy-scan'] === 'enabled' && !ann?.['backstage.io/trivy-config']) {
          return this.createError({ message: 'Trivy config is required when Trivy scan is enabled. Please add backstage.io/trivy-config annotation.' });
        }
        return true;
      })
      .test('sonarqube-consistency-1', 'SonarQube project key is required when instance is set. Please add sonarqube.org/project-key annotation.', function (annotations) {
        const ann = annotations as Record<string, string> | undefined;
        if (ann?.['sonarqube.org/instance'] && !ann?.['sonarqube.org/project-key']) {
          return this.createError({ message: 'SonarQube project key is required when instance is set. Please add sonarqube.org/project-key annotation.' });
        }
        return true;
      })
      .test('sonarqube-consistency-2', 'SonarQube instance is required when project key is set. Please add sonarqube.org/instance annotation.', function (annotations) {
        const ann = annotations as Record<string, string> | undefined;
        if (ann?.['sonarqube.org/project-key'] && !ann?.['sonarqube.org/instance']) {
          return this.createError({ message: 'SonarQube instance is required when project key is set. Please add sonarqube.org/instance annotation.' });
        }
        return true;
      })
      .test('sentry-consistency-1', 'Sentry project slug is required when organization is set. Please add sentry.io/project-slug annotation.', function (annotations) {
        const ann = annotations as Record<string, string> | undefined;
        if (ann?.['sentry.io/organization'] && !ann?.['sentry.io/project-slug']) {
          return this.createError({ message: 'Sentry project slug is required when organization is set. Please add sentry.io/project-slug annotation.' });
        }
        return true;
      })
      .test('sentry-consistency-2', 'Sentry organization is required when project slug is set. Please add sentry.io/organization annotation.', function (annotations) {
        const ann = annotations as Record<string, string> | undefined;
        if (ann?.['sentry.io/project-slug'] && !ann?.['sentry.io/organization']) {
          return this.createError({ message: 'Sentry organization is required when project slug is set. Please add sentry.io/organization annotation.' });
        }
        return true;
      })
      .optional(),
  }),
  spec: yup.object({
    type: yup.string().required('Component type is required. Please specify the type (e.g., website, service).'),
    owner: yup.string().required('Component owner is required. Please specify the owner team.'),
  }),
});

export function validateCatalogEntity(entity: Entity): ValidationError[] {
  // Validate Component type
  if (entity.kind === 'Component') {
    try {
      componentSchema.validateSync(entity, { abortEarly: false, strict: false });
      return []; // No validation errors
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        // Convert all Yup errors to warnings (frontend doesn't block imports)
        return convertYupErrors(err);
      }
      return [];
    }
  }

  return [];
}
